import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';
import { sessionKey } from './session';

export type ApiKey = Schemas['ApiKeyDto'];
export type Channels = Schemas['Channels'];
export type NotificationSettings = Record<string, Channels>;

/** Zweiter Faktor bei der Anmeldung (SEC-03). */
export function useLoginSecondFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => unwrap(api.POST('/api/v1/auth/login/2fa', { body: { code } })),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) => unwrap(api.POST('/api/v1/auth/password-reset/request', { body: { email } })),
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: (body: Schemas['PasswordResetConfirm']) =>
      unwrap(api.POST('/api/v1/auth/password-reset/confirm', { body })),
  });
}

export function useVerifyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => unwrap(api.POST('/api/v1/auth/verify-email', { body: { token } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
}

export function useResendVerification() {
  return useMutation({ mutationFn: () => unwrap(api.POST('/api/v1/me/verify-email')) });
}

export function useTotpSetup() {
  return useMutation({ mutationFn: () => unwrap(api.POST('/api/v1/me/2fa/setup')) });
}

export function useTotpEnable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => unwrap(api.POST('/api/v1/me/2fa/enable', { body: { code } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
}

export function useTotpDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => unwrap(api.POST('/api/v1/me/2fa/disable', { body: { password } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
}

const apiKeysKey = ['me', 'api-keys'] as const;

export function useApiKeys() {
  return useQuery({ queryKey: apiKeysKey, queryFn: () => unwrap(api.GET('/api/v1/me/api-keys')) });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['CreateApiKeyRequest']) => unwrap(api.POST('/api/v1/me/api-keys', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: apiKeysKey }),
  });
}

export function useDeleteApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/v1/me/api-keys/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: apiKeysKey }),
  });
}

const notificationSettingsKey = ['me', 'notification-settings'] as const;

export function useNotificationSettings() {
  return useQuery({
    queryKey: notificationSettingsKey,
    queryFn: () => unwrap(api.GET('/api/v1/me/notification-settings')) as Promise<NotificationSettings>,
  });
}

export function useSaveNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationSettings) => unwrap(api.PUT('/api/v1/me/notification-settings', { body })),
    onSuccess: (data) => qc.setQueryData(notificationSettingsKey, data),
  });
}

const sessionsKey = ['me', 'sessions'] as const;

export function useMySessions() {
  return useQuery({ queryKey: sessionsKey, queryFn: () => unwrap(api.GET('/api/v1/me/sessions')) });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/v1/me/sessions/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionsKey }),
  });
}

export function useRevokeOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/v1/me/sessions')),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionsKey }),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => unwrap(api.POST('/api/v1/me/delete-account', { body: { password } })),
    onSuccess: () => qc.clear(),
  });
}
